import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import "../../css/theme.css";
import { BaseUrl } from "../../BaseUrl";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import {
  MainContent,
} from "./StyledComponents";
import { useTranslation } from 'react-i18next';

// ====== Styled Components ======
const DataTable = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--color-shadow);
  overflow: hidden;
  width: 95%;
  max-width: 1200px;
  margin: 1.5rem auto;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-base);
`;

const TableHead = styled.thead`
  background: var(--color-primary);
  color: #fff;
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.95rem;
`;

const TableBody = styled.tbody`
  background: var(--color-surface);
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9f9f9;
  }

  &:hover {
    background: rgba(21, 101, 192, 0.05);
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: var(--color-text-main);
  font-size: 0.95rem;
  border-bottom: 1px solid #eee;
  vertical-align: top;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ProfileImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--color-text-light);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 600;
  color: var(--color-text-main);
  margin-bottom: 0.25rem;
`;

const UserEmail = styled.span`
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const AddressContainer = styled.div`
  max-width: 200px;
  line-height: 1.4;
`;

const AddressLine = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-text-main);
  
  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }
`;

const PlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PlanBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.free {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  &.basic {
    background: #e3f2fd;
    color: #1565c0;
  }
  
  &.pro {
    background: #f3e5f5;
    color: #7b1fa2;
  }
  
  &.enterprise {
    background: #fff3e0;
    color: #ef6c00;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  
  &.active {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  &.trialing {
    background: #fff3e0;
    color: #ef6c00;
  }
  
  &.inactive {
    background: #ffebee;
    color: #c62828;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  transition: 0.2s ease;
  color: var(--color-error);
  
  &:hover {
    color: #b71c1c;
    transform: scale(1.1);
  }
`;

const NoActiveButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ccc;
  background: #f5f5f5;
  color: #666;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: not-allowed;
  opacity: 0.6;
`;

// ====== Component ======
const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const { t } = useTranslation();
  const token = localStorage.getItem("pflegeUserToken");

  // ✅ Fetch Users
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BaseUrl}users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Access the nested data array from the API response
      setUsers(res.data?.data?.data || []);
    } catch (err) {
      console.error("Users fetch error", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ Get plan class for styling
  const getPlanClass = (plan) => {
    const planLower = plan?.toLowerCase() || "";
    if (planLower.includes("free")) return "free";
    if (planLower.includes("basic")) return "basic";
    if (planLower.includes("pro")) return "pro";
    if (planLower.includes("enterprise")) return "enterprise";
    return "free";
  };

  // ✅ Get status class for styling
  const getStatusClass = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("active")) return "active";
    if (statusLower.includes("trialing")) return "trialing";
    return "inactive";
  };

  // ✅ Format address
  const formatAddress = (user) => {
    const parts = [];
    if (user.address_street) parts.push(user.address_street);
    if (user.address_postcode) parts.push(user.address_postcode);
    if (user.address_city) parts.push(user.address_city);
    if (user.country) parts.push(user.country);
    
    return parts.join(", ");
  };

  // ✅ Delete User
  const handleDeleteUser = (id) => {
    setDeleteUserId(id);
  };

  const confirmDeleteUser = async () => {
    try {
      await axios.delete(`${BaseUrl}deleteuser/${deleteUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteUserId(null);
      fetchUsers();
    } catch (err) {
      alert("Fehler beim Löschen!");
    }
  };

  return (
    <MainContent>
      <DataTable>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeaderCell>{t("auth.name")}</TableHeaderCell>
                <TableHeaderCell>{t("details.address")}</TableHeaderCell>
                <TableHeaderCell>{t("spots.table.plan")}</TableHeaderCell>
                <TableHeaderCell>{t("subscription_list.table.status")}</TableHeaderCell>
                {/* <TableHeaderCell>Aktion</TableHeaderCell> */}
              </tr>
            </TableHead>

            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <UserInfo>
                        <ProfileImage>
                          {user.image ? (
                            <img 
                              src={`${BaseUrl}${user.image}`} 
                              alt={user.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.parentElement.textContent = user.name?.charAt(0) || "?";
                              }}
                            />
                          ) : (
                            user.name?.charAt(0) || "?"
                          )}
                        </ProfileImage>
                        <UserDetails>
                          <UserName>{user.name}</UserName>
                          <UserEmail>{user.email}</UserEmail>
                          <span style={{ fontSize: "0.85rem", color: "#666" }}>
                            {user.facility_name}
                          </span>
                        </UserDetails>
                      </UserInfo>
                    </TableCell>
                    
                    <TableCell>
                      <AddressContainer>
                        <AddressLine>{user.address_street}</AddressLine>
                        <AddressLine>
                          {[user.address_postcode, user.address_city]
                            .filter(Boolean)
                            .join(" ")}
                        </AddressLine>
                        <AddressLine>{user.country}</AddressLine>
                      </AddressContainer>
                    </TableCell>
                    
                    <TableCell>
                      <PlanContainer>
                        <PlanBadge className={getPlanClass(user.current_plan)}>
                          {user.current_plan || "Free"}
                        </PlanBadge>
                        {user.current_period_end && (
                          <span style={{ fontSize: "0.8rem", color: "#666" }}>
                            Bis: {new Date(user.current_period_end).toLocaleDateString()}
                          </span>
                        )}
                      </PlanContainer>
                    </TableCell>
                    
                    <TableCell>
                      <StatusBadge className={getStatusClass(user.plan_status)}>
                        {user.plan_status || "inactive"}
                      </StatusBadge>
                    </TableCell>
                    
                    {/* <TableCell>
                      <ActionButtons>
                        <NoActiveButton>Inaktiv</NoActiveButton>
                        <DeleteButton onClick={() => handleDeleteUser(user.id)}>
                          🗑️
                        </DeleteButton>
                      </ActionButtons>
                    </TableCell> */}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                    Keine Benutzer gefunden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTable>
      
      {deleteUserId && (
        <ConfirmDeleteModal
          confirmDelete={confirmDeleteUser}
          setDeleteConfirmId={setDeleteUserId}
        />
      )}
    </MainContent>
  );
};

export default AllUsers;